
import { useState, useEffect, useRef, useCallback } from "react";

const DEFAULT_PAGE_SIZE = 20;
export interface TablePaginationRequest {
  page: number;
  pageSize: number;
}

export interface TablePaginationResponse<T> {
  data: T[];
  total: number;
}

interface UseTablePaginationOptions<T> {
  pageSize?: number;
  fetcher: (params: TablePaginationRequest) => Promise<TablePaginationResponse<T>>;
  deps?: any[]; // trigger reload on filters/search change
}

export const getPaginationLimitSkip = (params: TablePaginationRequest) => {
    return {
        skip: (params.page - 1) * DEFAULT_PAGE_SIZE,
        limit: params.pageSize
    }
}
export function useTablePagination<T>({
  pageSize = DEFAULT_PAGE_SIZE,
  fetcher,
  deps = [],
}: UseTablePaginationOptions<T>) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Keep latest fetcher in ref so hook doesn't depend on its identity
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  // mounted guard to avoid setState after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // perform a fetch for page `p`
  const doFetch = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const res = await fetcherRef.current({ page: p, pageSize: size });
      if (!mountedRef.current) return;
      setData(res.data);
      setTotal(res.total);
    } catch (err) {
      // handle or surface error as needed; keep loading toggling robust
      console.error("useTablePagination fetch error:", err);
      if (!mountedRef.current) return;
      setData([]);
      setTotal(0);
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
    }
  }, [size]);

  useEffect(() => {
    setPage(1);
    // fetch page 1 using latest fetcher
    doFetch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, size]); // include size so a pageSize change triggers a reload

  // When page or size changes due to pagination controls
  useEffect(() => {
    doFetch(page);
  }, [page, doFetch]);

  // Expose pageSize control safely
  const onChange = (p: number, ps?: number) => {
    if (ps && ps !== size) {
      setSize(ps);
      setPage(1);
      return;
    }
    setPage(p);
  };

  return {
    data,
    total,
    loading,
    page,
    pageSize: size,
    onChange,
    setData: setData, // manual
    // setDataWithInitial: (d: T[]) => setData(d),
    setTotal, // manual
    reload: () => doFetch(page),
    setPage,
    setPageSize: setSize,
  };
}